import React, { useState } from "react";

const FlashTestForm = ({ onSubmit, isGenerating }) => {
  const [formData, setFormData] = useState({
    examName: "",
    subject: "",
    category: "",
    subTopics: [],
    difficulty: "",
    questionCount: "",
    duration: "",
    colorScheme: 5,
  });

  const [tagInput, setTagInput] = useState("");
  const [errors, setErrors] = useState({});

  const categories = ["Technical", "Aptitude", "Reasoning"];
  const difficulties = ["Easy", "Medium", "Hard"];


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleAddTag = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const tagValue = tagInput.trim().replace(/,$/, "");
      if (tagValue) {
        if (tagValue.length < 3 || tagValue.length > 50) {
          setErrors((prev) => ({
            ...prev,
            subTopics: "Each subtopic must be between 3 and 50 characters."
          }));
          return;
        }
        if (!formData.subTopics.includes(tagValue)) {
          setFormData((prev) => ({
            ...prev,
            subTopics: [...prev.subTopics, tagValue],
          }));
          if (errors.subTopics) {
            setErrors((prev) => ({ ...prev, subTopics: "" }));
          }
        }
        setTagInput("");
      }
    }
  };

  const handleRemoveTag = (indexToRemove) => {
    if (isGenerating) return;
    setFormData((prev) => ({
      ...prev,
      subTopics: prev.subTopics.filter((_, index) => index !== indexToRemove),
    }));
  };

  const validate = (finalSubTopics = formData.subTopics) => {
    const newErrors = {};
    if (!formData.examName.trim()) {
      newErrors.examName = "Assessment Title is required.";
    } else if (formData.examName.trim().length < 3 || formData.examName.trim().length > 50) {
      newErrors.examName = "Assessment Title must be between 3 and 50 characters.";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Technology is required.";
    } else if (formData.subject.trim().length < 3 || formData.subject.trim().length > 50) {
      newErrors.subject = "Technology must be between 3 and 50 characters.";
    }

    if (!formData.category) newErrors.category = "Technology Domain is required.";

    if (finalSubTopics.length === 0) {
      newErrors.subTopics = "At least one Technical Topics is required.";
    } else {
      const invalidTag = finalSubTopics.find(t => t.length < 3 || t.length > 50);
      if (invalidTag) {
        newErrors.subTopics = "Each subtopic must be between 3 and 50 characters.";
      }
    }

    if (!formData.difficulty) newErrors.difficulty = "Skill Level is required.";
    if (!formData.questionCount || isNaN(formData.questionCount) || parseInt(formData.questionCount) < 1)
      newErrors.questionCount = "Please enter a valid number of questions (minimum 1).";
    if (!formData.duration || isNaN(formData.duration) || parseInt(formData.duration) < 1)
      newErrors.duration = "Please enter a valid duration in minutes (minimum 1).";

    let colorSchemeVal = parseInt(formData.colorScheme);
    if (isNaN(colorSchemeVal) || ![2, 5, 7].includes(colorSchemeVal)) {
      newErrors.colorScheme = "Please select a valid color scheme.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isGenerating) return;

    // Try to add any remaining text in the tag input
    let finalSubTopics = [...formData.subTopics];
    const remainingTag = tagInput.trim();
    if (remainingTag) {
      if (!finalSubTopics.includes(remainingTag)) {
        finalSubTopics.push(remainingTag);
      }
    }

    if (validate(finalSubTopics)) {
      let colorSchemeVal = parseInt(formData.colorScheme);
      if (isNaN(colorSchemeVal) || ![2, 5, 7].includes(colorSchemeVal)) {
        colorSchemeVal = 5;
      }
      const finalFormData = {
        ...formData,
        subTopics: finalSubTopics,
        questionCount: String(parseInt(formData.questionCount) * 2),
        colorScheme: colorSchemeVal
      };
      onSubmit(finalFormData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flash-test-form">
      {/* Row 1: Assessment Title | Technology | Technology Domain */}
      <div className="form-row-3col">
        {/* Exam Name */}
        <div className={`flash-form-group ${errors.examName ? "error" : ""}`}>
          <label htmlFor="examName">Assessment Title<span>*</span></label>
          <input
            type="text"
            id="examName"
            name="examName"
            value={formData.examName}
            onChange={handleInputChange}
            placeholder="e.g., Java Developer Assessment"
            disabled={isGenerating}
          />
          {errors.examName && <span className="error-text">{errors.examName}</span>}
        </div>

        {/* Subject */}
        <div className={`flash-form-group ${errors.subject ? "error" : ""}`}>
          <label htmlFor="subject">Technology<span>*</span></label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            placeholder="e.g., Java, Python, React, SQL"
            disabled={isGenerating}
          />
          {errors.subject && <span className="error-text">{errors.subject}</span>}
        </div>

        {/* Category */}
        <div className={`flash-form-group ${errors.category ? "error" : ""}`}>
          <label htmlFor="category">Technology Domain<span>*</span></label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleInputChange}
            disabled={isGenerating}
          >
            <option value="">Select a technology domain</option>
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
          {errors.category && <span className="error-text">{errors.category}</span>}
        </div>
      </div>

      {/* Row 2: Skill Level | Question Count | Time Limit */}
      <div className="form-row-3col">
        {/* Difficulty Level */}
        <div className={`flash-form-group ${errors.difficulty ? "error" : ""}`}>
          <label htmlFor="difficulty">Skill Level<span>*</span></label>
          <select
            id="difficulty"
            name="difficulty"
            value={formData.difficulty}
            onChange={handleInputChange}
            disabled={isGenerating}
          >
            <option value="">Select the target skill level</option>
            {difficulties.map((diff) => (
              <option key={diff} value={diff}>
                {diff}
              </option>
            ))}
          </select>
          {errors.difficulty && <span className="error-text">{errors.difficulty}</span>}
        </div>

        {/* Number of Questions */}
        <div className={`flash-form-group ${errors.questionCount ? "error" : ""}`}>
          <label htmlFor="questionCount"> Question Count<span>*</span></label>
          <input
            type="number"
            id="questionCount"
            name="questionCount"
            value={formData.questionCount}
            onChange={handleInputChange}
            placeholder="e.g., 20"
            min="1"
            disabled={isGenerating}
          />
          {errors.questionCount && <span className="error-text">{errors.questionCount}</span>}
        </div>

        {/* Duration (in minutes) */}
        <div className={`flash-form-group ${errors.duration ? "error" : ""}`}>
          <label htmlFor="duration">Time Limit (Minutes)<span>*</span></label>
          <input
            type="number"
            id="duration"
            name="duration"
            value={formData.duration}
            onChange={handleInputChange}
            placeholder="e.g., 30"
            min="1"
            disabled={isGenerating}
          />
          {errors.duration && <span className="error-text">{errors.duration}</span>}
        </div>
      </div>

      {/* Subtopics (Multi Select Tags Input) — full width, at the end */}
      <div className={`flash-form-group ${errors.subTopics ? "error" : ""}`}>
        <label htmlFor="subTopics">Technical Topics (Press Enter to add multiple)<span>*</span></label>
        <div className={`tags-input-container ${errors.subTopics ? "error" : ""}`}>
          {formData.subTopics.map((tag, index) => (
            <div key={index} className="tag-item">
              <span>{tag}</span>
              <button
                type="button"
                className="tag-remove-btn"
                onClick={() => handleRemoveTag(index)}
                disabled={isGenerating}
              >
                &times;
              </button>
            </div>
          ))}
          <input
            type="text"
            id="subTopics"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleAddTag}
            placeholder="e.g., OOP, Collections, REST API"
            disabled={isGenerating}
          />
        </div>
        <span className="subtopics-help-text">Type a topic and press Enter or comma (,) to add.</span>
        {errors.subTopics && <span className="error-text">{errors.subTopics}</span>}
      </div>



      {/* Color Scheme Section */}
      <div className={`flash-form-group ${errors.colorScheme ? "error" : ""}`} style={{ gridColumn: "1 / -1", marginTop: "15px", marginBottom: "20px" }}>
        <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", fontSize: "14px", color: "#374151" }}>Color Scheme<span>*</span></label>
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
          gap: "16px",
          marginTop: "8px"
        }}>
          {[
            {
              id: 5,
              name: "bitLabs Theme",
              headerBg: "#ffffff",
              headerText: "#1e293b",
              highlightBg: "#ea580c",
              expBg: "#ffedd5"
            },
            {
              id: 7,
              name: "TekWorks Theme",
              headerBg: "#e2e8f0",
              headerText: "#1e293b",
              highlightBg: "#cbd5e1",
              expBg: "#e0e7ff"
            },
            {
              id: 2,
              name: "Symphonize Theme",
              headerBg: "#2563eb",
              headerText: "#ffffff",
              highlightBg: "#cbd5e1",
              expBg: "#dbeafe"
            }
          ].map((scheme) => {
            const isSelected = parseInt(formData.colorScheme) === scheme.id;
            return (
              <div
                key={scheme.id}
                onClick={() => {
                  if (isGenerating) return;
                  setFormData(prev => ({ ...prev, colorScheme: scheme.id }));
                  if (errors.colorScheme) {
                    setErrors(prev => ({ ...prev, colorScheme: "" }));
                  }
                }}
                style={{
                  border: isSelected ? "2px solid #3b82f6" : "1px solid #cbd5e1",
                  borderRadius: "8px",
                  padding: "12px",
                  cursor: isGenerating ? "not-allowed" : "pointer",
                  background: isSelected ? "#f8fafc" : "#ffffff",
                  display: "flex",
                  gap: "10px",
                  alignItems: "flex-start",
                  boxShadow: isSelected ? "0 4px 6px -1px rgba(0,0,0,0.05)" : "none"
                }}
              >
                <input
                  type="radio"
                  name="colorScheme"
                  value={scheme.id}
                  checked={isSelected}
                  onChange={() => { }}
                  disabled={isGenerating}
                  style={{ marginTop: "3px", cursor: "pointer" }}
                />
                <div style={{ flex: 1 }}>
                  {/* Preview Box */}
                  <div style={{
                    border: isSelected && scheme.id === 5 ? "3px solid #fef08a" : "1px solid #cbd5e1",
                    borderRadius: "4px",
                    overflow: "hidden",
                    fontSize: "7px",
                    fontFamily: "sans-serif",
                    background: "#ffffff",
                    marginBottom: "6px"
                  }}>
                    {/* Header Bar */}
                    <div style={{
                      background: scheme.headerBg,
                      color: scheme.headerText,
                      padding: "4px 6px",
                      fontWeight: "bold",
                      borderBottom: "1px solid #e2e8f0"
                    }}>
                      {scheme.name}
                    </div>
                    {/* Options Preview */}
                    <div style={{ padding: "6px", display: "flex", flexDirection: "column", gap: "2px" }}>
                      <div style={{ color: "#64748b" }}>George Washington</div>
                      <div style={{ color: "#64748b" }}>Thomas Jefferson</div>
                      <div style={{
                        background: scheme.highlightBg,
                        color: scheme.highlightBg === "#ea580c" ? "#ffffff" : "#1e293b",
                        padding: "1px 3px",
                        borderRadius: "2px",
                        fontWeight: scheme.highlightBg === "#ea580c" ? "600" : "normal"
                      }}>
                        Abraham Lincoln
                      </div>
                      <div style={{ color: "#64748b" }}>Ulysses S. Grant</div>
                      <div style={{
                        background: scheme.expBg,
                        padding: "2px 3px",
                        borderRadius: "2px",
                        marginTop: "1px",
                        color: "#334155"
                      }}>
                        This is an explanation.<br />Carry on.
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: "12px", fontWeight: "600", color: "#1e293b" }}>{scheme.name}</div>
                </div>
              </div>
            );
          })}
        </div>
        {errors.colorScheme && <span className="error-text" style={{ color: "#ef4444", fontSize: "12px", marginTop: "4px", display: "block" }}>{errors.colorScheme}</span>}
      </div>

      {/* Generate Button */}
      <div className="flash-form-actions">
        <button type="submit" className="btn-generate" disabled={isGenerating} style={{ background: "linear-gradient(286deg, rgb(251, 187, 92) 0%, rgb(230, 106, 14) 100%)" , color : "white"}}>
          {isGenerating ? (
            <>
              <span className="spinner-container" />
              Generating Flash Test...
            </>
          ) : (
            "Generate Flash Test"
          )}
        </button>
      </div>
    </form>
  );
};

export default FlashTestForm;
